<template>
  <!-- Master-only card designer modal. Mounted OUTSIDE the dashboard grid (grid-items use
       CSS transforms, which would trap a fixed overlay). Two card kinds:
         • Content — Froala rich text.
         • Data — a structured binding over any table (validated + parameterized server-side),
                  with an AI assistant that proposes a binding from a plain-English prompt. -->
  <div class="rce-backdrop" @click.self="$emit('close')">
    <div class="rce" role="dialog" aria-label="Card editor">
      <div class="rce-head">
        <span class="rce-title">{{ isEdit ? 'Edit card' : 'Add a card' }}</span>
        <button type="button" class="rce-x" @click="$emit('close')" aria-label="Close">×</button>
      </div>

      <div class="rce-body">
        <label v-if="mode !== 'template'" class="rce-f"><span>Card title</span>
          <input type="text" v-model="title" placeholder="e.g. Open quotes by rep" maxlength="200" />
        </label>

        <div class="rce-types">
          <button type="button" :class="{ active: mode === 'content' }" @click="mode = 'content'">📝 Text / content</button>
          <button type="button" :class="{ active: mode === 'data' }" @click="switchToData">📊 Data from a table</button>
          <button v-if="!isEdit && templates && templates.length" type="button" :class="{ active: mode === 'template' }" @click="mode = 'template'">📋 From a Layout 1 card</button>
        </div>

        <!-- ===== "FROM A LAYOUT 1 CARD" CATALOG — places the REAL card on this layout (identical rendering) ===== -->
        <div v-if="mode === 'template'" class="rce-templates">
          <p class="rce-ai-note" style="margin:0 0 10px;">Add a card from <strong>Layout 1</strong> to this layout — placed exactly as it looks there (you can move, resize, or remove it; Layout 1 isn't changed). For a built-in card this <strong>restores the original</strong>, so it's a safe way to recover a card after an edit goes wrong.</p>
          <div v-if="tplCategories.length" class="rce-tpl-tabs">
            <button v-for="cat in tplCategories" :key="cat" type="button" class="rce-tpl-tab" :class="{ active: activeTplTab === cat }" @click="tplTab = cat">{{ cat }}</button>
          </div>
          <div class="rce-tpl-list">
            <button v-for="t in tplForTab" :key="t.key" type="button" class="rce-tpl" :class="{ 'is-on': t.alreadyOn && !t.isBuiltin }" :disabled="t.alreadyOn && !t.isBuiltin" @click="pickTemplate(t)">
              <span class="rce-tpl-title">{{ t.title }}</span>
              <span v-if="t.isBuiltin" class="rce-tpl-badge rce-tpl-builtin">{{ t.alreadyOn ? '↺ Restore original' : 'Add' }}</span>
              <span v-else-if="t.alreadyOn" class="rce-tpl-badge rce-tpl-on">✓ On this layout</span>
              <span v-else class="rce-tpl-badge rce-tpl-custom">Custom</span>
            </button>
            <div v-if="!tplForTab.length" class="rce-muted">No cards in this group.</div>
          </div>
          <div v-if="!templates.length" class="rce-muted">No Layout 1 cards available yet.</div>
        </div>

        <!-- ===== CONTENT / DATA — create a new editable custom card ===== -->
        <template v-else>
        <div v-if="mode === 'content'" class="rce-content">
          <div class="rce-ai">
            <div class="rce-ai-row">
              <input type="text" v-model="aiHtmlPrompt" :disabled="aiHtmlBusy"
                     placeholder="Ask AI to write or adjust this card — e.g. “a titled callout box with our phone number”"
                     @keyup.enter="askAiHtml" />
              <button type="button" class="rce-ai-btn" :disabled="aiHtmlBusy || !aiHtmlPrompt.trim()" @click="askAiHtml">
                <span v-if="aiHtmlBusy" class="rce-spin" aria-hidden="true"></span>{{ aiHtmlBusy ? 'Thinking…' : '✨ Ask AI' }}
              </button>
            </div>
            <p class="rce-ai-note">AI edits the card's HTML and keeps your existing look. Use the <code>&lt;/&gt;</code> “Code View” button in the toolbar to hand-edit the source.</p>
          </div>
          <div v-if="tokens && tokens.length" class="rce-tokens">
            <div class="rce-tokens-h">Live values — click to insert a <code>{token}</code> that fills from the data (and updates with the filters):</div>
            <div class="rce-tokens-list">
              <button v-for="tk in tokens" :key="tk.token" type="button" class="rce-token" :title="tk.label + ' = ' + tk.value" @click="insertToken(tk.token)">
                <span class="rce-token-name">{{ tk.token }}</span><span class="rce-token-val">{{ tk.value }}</span>
              </button>
            </div>
          </div>
          <froala :config="froalaConfig" v-model="contentHtml" :tag="'textarea'"></froala>
        </div>

        <!-- ===== DATA MODE ===== -->
        <div v-else class="rce-data">
          <!-- AI assistant -->
          <div class="rce-ai">
            <div class="rce-ai-row">
              <input type="text" v-model="aiPrompt" :disabled="aiBusy"
                     placeholder="Ask AI to build this — e.g. “total invoiced sales by sales rep, top 10”"
                     @keyup.enter="askAi" />
              <button type="button" class="rce-ai-btn" :disabled="aiBusy || !aiPrompt.trim()" @click="askAi">
                <span v-if="aiBusy" class="rce-spin" aria-hidden="true"></span>{{ aiBusy ? 'Thinking…' : '✨ Ask AI' }}
              </button>
            </div>
            <div v-if="aiWarning" class="rce-ai-warn">⚠ {{ aiWarning }} — adjust below.</div>
            <p class="rce-ai-note">AI suggests which table/columns to use — you review &amp; preview before saving. It never runs SQL or sees your data.</p>
          </div>

          <div v-if="schemaBusy" class="rce-muted">Loading the table list…</div>

          <!-- Table picker -->
          <label class="rce-f"><span>Table / view</span>
            <input type="text" v-model="tableSearch" class="rce-tsearch" placeholder="Filter tables…" />
            <select :value="spec.table" @change="selectTable($event.target.value)">
              <option value="">Choose a table…</option>
              <option v-for="t in filteredTables" :key="t.name" :value="t.name">{{ t.name }}</option>
            </select>
          </label>

          <template v-if="spec.table">
            <!-- Columns -->
            <div class="rce-sec">
              <div class="rce-sec-h"><span>Columns</span>
                <span class="rce-sec-tools">
                  <button type="button" @click="addColumn">+ Column</button>
                  <button type="button" @click="addCountRows">+ Count rows</button>
                </span>
              </div>
              <div v-for="(c, i) in spec.columns" :key="'c' + i" class="rce-colrow">
                <select v-model="c.col" class="rce-colsel">
                  <option value="*">* (all rows)</option>
                  <option v-for="col in currentColumns" :key="col.name" :value="col.name">{{ col.name }}</option>
                </select>
                <select v-model="c.agg" class="rce-aggsel">
                  <option value="">(no total)</option>
                  <option value="sum">Sum</option>
                  <option value="avg">Average</option>
                  <option value="min">Min</option>
                  <option value="max">Max</option>
                  <option value="count">Count</option>
                </select>
                <input type="text" v-model="c.alias" class="rce-alias" placeholder="Label (optional)" />
                <button type="button" class="rce-del" @click="spec.columns.splice(i, 1)" title="Remove">×</button>
              </div>
              <div v-if="!spec.columns.length" class="rce-muted">Add at least one column.</div>
            </div>

            <!-- Filters -->
            <div class="rce-sec">
              <div class="rce-sec-h"><span>Filters <em>(optional)</em></span>
                <span class="rce-sec-tools"><button type="button" @click="addFilter">+ Filter</button></span>
              </div>
              <div v-for="(f, i) in spec.filters" :key="'f' + i" class="rce-colrow">
                <select v-model="f.col" class="rce-colsel">
                  <option value="">Column…</option>
                  <option v-for="col in currentColumns" :key="col.name" :value="col.name">{{ col.name }}</option>
                </select>
                <select v-model="f.op" class="rce-aggsel">
                  <option value="=">=</option><option value="<>">≠</option>
                  <option value=">">&gt;</option><option value="<">&lt;</option>
                  <option value=">=">≥</option><option value="<=">≤</option>
                  <option value="LIKE">contains</option><option value="IN">in list</option>
                </select>
                <input type="text" v-model="f.value" class="rce-alias" placeholder="Value (comma-separate for “in list”)" />
                <button type="button" class="rce-del" @click="spec.filters.splice(i, 1)" title="Remove">×</button>
              </div>
            </div>

            <!-- Shape -->
            <div class="rce-grid2">
              <label class="rce-f"><span>Show as</span>
                <select v-model="spec.viz">
                  <option value="table">Table</option>
                  <option value="kpi">Single number (KPI)</option>
                  <option value="barlist">Ranked bars</option>
                </select>
              </label>
              <label class="rce-f" v-if="spec.viz !== 'table'"><span>Number format</span>
                <select v-model="spec.format">
                  <option value="number">Plain number</option>
                  <option value="int">Whole number</option>
                  <option value="money">Currency</option>
                </select>
              </label>
              <label class="rce-f"><span>Sort by</span>
                <select v-model="spec.orderByCol">
                  <option value="">(none)</option>
                  <option v-for="a in aliasChoices" :key="a" :value="a">{{ a }}</option>
                </select>
              </label>
              <label class="rce-f" v-if="spec.orderByCol"><span>Direction</span>
                <select v-model="spec.orderByDir"><option value="desc">High → low</option><option value="asc">Low → high</option></select>
              </label>
              <label class="rce-f"><span>Max rows</span>
                <input type="number" v-model.number="spec.limit" min="1" max="1000" />
              </label>
            </div>

            <!-- Preview -->
            <div class="rce-previewbar">
              <button type="button" class="rce-prev-btn" :disabled="previewBusy" @click="preview">
                <span v-if="previewBusy" class="rce-spin" aria-hidden="true"></span>{{ previewBusy ? 'Running…' : '▶ Preview' }}
              </button>
              <span v-if="previewErr" class="rce-prev-err">{{ previewErr }}</span>
            </div>
            <div v-if="previewData" class="rce-preview">
              <!-- kpi -->
              <div v-if="previewData.viz === 'kpi'" class="rce-pv-kpi">{{ fmtVal(previewData.data.value, previewData.data.format) }}<small>{{ previewData.data.label }}</small></div>
              <!-- barlist -->
              <div v-else-if="previewData.viz === 'barlist'" class="rce-pv-bars">
                <div v-for="(it, i) in (previewData.data.items || [])" :key="'pb' + i" class="rce-pv-bar">
                  <span class="rce-pv-lbl" :title="it.label">{{ it.label }}</span>
                  <span class="rce-pv-track"><span class="rce-pv-fill" :style="{ width: barPct(it.value) }"></span></span>
                  <span class="rce-pv-val">{{ fmtVal(it.value, previewData.data.format) }}</span>
                </div>
                <div v-if="!(previewData.data.items || []).length" class="rce-muted">No rows.</div>
              </div>
              <!-- table -->
              <div v-else class="rce-pv-tablewrap">
                <table class="rce-pv-table">
                  <thead><tr><th v-for="(c, i) in (previewData.data.columns || [])" :key="'h' + i">{{ c }}</th></tr></thead>
                  <tbody>
                    <tr v-for="(r, ri) in (previewData.data.rows || []).slice(0, 50)" :key="'r' + ri">
                      <td v-for="(cell, ci) in r" :key="'cell' + ci">{{ cell }}</td>
                    </tr>
                    <tr v-if="!(previewData.data.rows || []).length"><td :colspan="(previewData.data.columns || []).length || 1" class="rce-muted">No rows.</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </template>
        </div>
        </template>
      </div>

      <div class="rce-foot">
        <span v-if="error" class="rce-err">{{ error }}</span>
        <span class="rce-spacer"></span>
        <button type="button" class="rce-btn" @click="$emit('close')">Cancel</button>
        <button v-if="mode !== 'template'" type="button" class="rce-btn rce-btn-primary" :disabled="saving" @click="save">
          <span v-if="saving" class="rce-spin" aria-hidden="true"></span>{{ saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create card') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import httpClient from "@/assets/js/HttpClient";

// A fresh, fully-reactive data spec (orderBy flattened to orderByCol/orderByDir for v-model;
// re-nested into { col, dir } only when sent to the server).
function freshSpec() {
  return { table: "", columns: [], filters: [], orderByCol: "", orderByDir: "desc", limit: 100, viz: "table", format: "number" };
}

export default {
  name: "ReportCardEditor",
  // card: the existing card to edit (or null to create). Shape from /dashboard-cards:
  //   { id, title, type, viz, contentHtml, spec }
  props: {
    card: { type: Object, default: null },
    // [{ token: "{totalSales}", label: "Total sales", value: "$13.6M" }] — live values for the palette.
    tokens: { type: Array, default: function () { return []; } },
    // Layout scope marker stamped on the created card: null/"default" = shared (Layout 1); a layout id = that layout only.
    scope: { type: String, default: null },
    // Cards on Layout 1 offered as starting points: [{ key, title, kind:'content'|'data', contentHtml, spec, viz }].
    templates: { type: Array, default: function () { return []; } },
  },
  data() {
    return {
      mode: (!this.card && this.templates && this.templates.length) ? "template" : "content",
      tplTab: "",
      title: "",
      contentHtml: "",
      spec: freshSpec(),
      tables: [],
      schemaBusy: false,
      schemaLoaded: false,
      tableSearch: "",
      aiPrompt: "",
      aiBusy: false,
      aiWarning: "",
      aiHtmlPrompt: "",
      aiHtmlBusy: false,
      previewBusy: false,
      previewErr: "",
      previewData: null,
      saving: false,
      error: "",
      froalaConfig: {
        key: "YNB3fJ3C8C8A5A4C2A-9UJHAEFZMUJOYGYQEa1c1ZJg1RAeF5C4C3D3E2C2B4D6A3A1==",
        immediateVueModelUpdate: true,
        heightMin: 160,
        placeholderText: "Write the card content…",
        toolbarButtons: {
          moreText: { buttons: ["bold", "italic", "underline", "strikeThrough", "fontSize", "textColor", "backgroundColor", "clearFormatting"] },
          moreParagraph: { buttons: ["alignLeft", "alignCenter", "alignRight", "formatOL", "formatUL", "paragraphFormat", "quote"] },
          moreRich: { buttons: ["insertLink", "insertTable", "insertHR"] },
          moreMisc: { buttons: ["undo", "redo", "html"], align: "right" },
        },
      },
    };
  },
  created() {
    // Capture the Froala instance so save() can flush Code View (its edits don't reach v-model until exit).
    var self = this;
    this.froalaConfig.events = Object.assign({}, this.froalaConfig.events || {}, {
      initialized: function () { self._froala = this; },
    });
  },
  computed: {
    isEdit() { return !!(this.card && this.card.id); },
    // "From a Layout 1 card" catalog, grouped into category tabs (only non-empty categories, in a fixed order).
    tplCategories() {
      var order = ["KPIs", "Charts & Maps", "Tables", "Insights", "Custom", "Other"];
      var present = {};
      (this.templates || []).forEach(function (t) { present[t.category || "Other"] = true; });
      return order.filter(function (c) { return present[c]; });
    },
    activeTplTab() { return this.tplTab || (this.tplCategories[0] || ""); },
    tplForTab() {
      var cat = this.activeTplTab;
      return (this.templates || []).filter(function (t) { return (t.category || "Other") === cat; });
    },
    filteredTables() {
      var s = (this.tableSearch || "").trim().toLowerCase();
      if (!s) return this.tables;
      return this.tables.filter(function (t) { return t.name.toLowerCase().indexOf(s) >= 0; });
    },
    currentColumns() {
      var arr = this.tables || [];
      for (var i = 0; i < arr.length; i++) if (arr[i].name === this.spec.table) return arr[i].columns || [];
      return [];
    },
    // Order-by choices = the labels the columns will carry (alias else column name).
    aliasChoices() {
      return (this.spec.columns || []).map((c) => {
        if (c.alias && c.alias.trim()) return c.alias.trim();
        if (c.agg && c.col === "*") return "count";
        if (c.agg) return c.agg + "_" + c.col;
        return c.col;
      }).filter(Boolean);
    },
  },
  mounted() {
    if (this.card) {
      this.title = this.card.title || "";
      this.mode = this.card.type === "data" ? "data" : "content";
      if (this.card.type === "data" && this.card.spec) this.spec = this.fromServerSpec(this.card.spec);
      else this.contentHtml = this.card.contentHtml || "";
    }
    if (this.mode === "data") this.loadSchema();
    // Escape any transformed/stacking report ancestor so the fixed overlay covers the whole viewport.
    try { if (this.$el && document.body) document.body.appendChild(this.$el); } catch (e) { /* noop */ }
  },
  beforeDestroy() {
    try { if (this.$el && this.$el.parentNode) this.$el.parentNode.removeChild(this.$el); } catch (e) { /* noop */ }
  },
  methods: {
    switchToData() { this.mode = "data"; this.loadSchema(); },
    // Place the REAL Layout 1 card on this layout — the parent adds its gid to the grid so the same render
    // branch draws it (identical formatting). No HTML copy, no editor.
    pickTemplate(t) {
      if (!t) return;
      if (t.isBuiltin) { this.$emit("restore", t); return; }   // built-in → parent restores MY original (adds if missing, resets to factory if already on)
      if (t.alreadyOn) return;
      this.$emit("place", t);
    },
    loadSchema() {
      if (this.schemaLoaded || this.schemaBusy) return;
      var self = this;
      this.schemaBusy = true;
      httpClient.get("/report/schema")
        .then(function (res) { if (res.data && res.data.ok) { self.tables = res.data.tables || []; self.schemaLoaded = true; } })
        .catch(function () { /* leave empty; the picker just shows nothing */ })
        .finally(function () { self.schemaBusy = false; });
    },
    selectTable(name) {
      this.spec.table = name || "";
      this.spec.columns = [];
      this.spec.filters = [];
      this.spec.orderByCol = "";
      this.previewData = null;
    },
    addColumn() { this.spec.columns.push({ col: "", agg: "", alias: "" }); },
    addCountRows() { this.spec.columns.push({ col: "*", agg: "count", alias: "Count" }); },
    addFilter() { this.spec.filters.push({ col: "", op: "=", value: "" }); },
    // Flatten the server's { col, dir } orderBy into the reactive flat fields.
    fromServerSpec(s) {
      var out = freshSpec();
      out.table = s.table || "";
      out.columns = Array.isArray(s.columns) ? s.columns.map((c) => ({ col: c.col || "", agg: c.agg || "", alias: c.alias || "" })) : [];
      out.filters = Array.isArray(s.filters) ? s.filters.map((f) => ({ col: f.col || "", op: f.op || "=", value: f.value == null ? "" : String(f.value) })) : [];
      out.limit = Number(s.limit) || 100;
      out.viz = s.viz || "table";
      out.format = s.format || "number";
      if (s.orderBy && s.orderBy.col) { out.orderByCol = s.orderBy.col; out.orderByDir = s.orderBy.dir === "asc" ? "asc" : "desc"; }
      return out;
    },
    // Re-nest the flat orderBy fields into the server's { col, dir } shape.
    toServerSpec() {
      var s = this.spec;
      var out = {
        table: s.table,
        columns: (s.columns || []).map((c) => ({ col: c.col, agg: c.agg, alias: c.alias })),
        filters: (s.filters || []).filter((f) => f.col).map((f) => ({ col: f.col, op: f.op, value: f.value })),
        limit: Number(s.limit) || 100,
        viz: s.viz,
        format: s.format,
      };
      if (s.orderByCol) out.orderBy = { col: s.orderByCol, dir: s.orderByDir === "asc" ? "asc" : "desc" };
      return out;
    },
    askAi() {
      if (!this.aiPrompt.trim()) return;
      var self = this;
      this.aiBusy = true;
      this.aiWarning = "";
      this.error = "";
      this.loadSchema();
      httpClient.post("/report/ai-bind", { prompt: this.aiPrompt })
        .then(function (res) {
          var d = res.data || {};
          if (d.ok && d.spec) {
            self.spec = self.fromServerSpec(d.spec);
            if (d.title && !self.title.trim()) self.title = d.title;
            self.aiWarning = d.warning || "";
            self.previewData = null;
          } else {
            self.error = d.message || "The AI couldn't build that.";
          }
        })
        .catch(function () { self.error = "Something went wrong asking the AI. Please try again."; })
        .finally(function () { self.aiBusy = false; });
    },
    // Content cards: AI writes/adjusts the card's HTML (preserving the existing look), sanitized server-side.
    askAiHtml() {
      if (!this.aiHtmlPrompt.trim()) return;
      var self = this;
      this.aiHtmlBusy = true;
      this.error = "";
      httpClient.post("/report/ai-html", { prompt: this.aiHtmlPrompt, html: this.contentHtml || "" })
        .then(function (res) {
          var d = res.data || {};
          if (d.ok && typeof d.html === "string") { self.contentHtml = d.html; self.aiHtmlPrompt = ""; }
          else self.error = d.message || "The AI couldn't adjust the card.";
        })
        .catch(function () { self.error = "Something went wrong asking the AI. Please try again."; })
        .finally(function () { self.aiHtmlBusy = false; });
    },
    // Insert a {token} placeholder. Appended — the seeded templates already place tokens, and the
    // Code View / WYSIWYG lets you reposition it. The token fills with live data when the card renders.
    insertToken(tok) {
      this.contentHtml = (this.contentHtml || "") + " " + tok;
    },
    preview() {
      var self = this;
      this.previewBusy = true;
      this.previewErr = "";
      this.previewData = null;
      httpClient.post("/report/run-binding", { spec: this.toServerSpec() })
        .then(function (res) {
          var d = res.data || {};
          if (d.ok) self.previewData = { viz: d.viz, data: d.data || {} };
          else self.previewErr = d.message || "Couldn't run that.";
        })
        .catch(function () { self.previewErr = "Something went wrong running the preview."; })
        .finally(function () { self.previewBusy = false; });
    },
    save() {
      if (!this.title.trim()) { this.error = "Give the card a title."; return; }
      if (this.mode === "data" && !this.spec.table) { this.error = "Pick a table for the data card."; return; }
      var self = this;
      this.saving = true;
      this.error = "";
      // Froala keeps Code View edits in its own buffer until code view is exited — flush it so HTML
      // hand-edited in Code View (e.g. {tokens} you typed there) is captured on Save, not dropped.
      if (this.mode === "content" && this._froala) {
        try {
          if (this._froala.codeView && this._froala.codeView.isActive()) this._froala.codeView.toggle();
          if (this._froala.html && typeof this._froala.html.get === "function") this.contentHtml = this._froala.html.get();
        } catch (e) { /* fall back to the v-model value */ }
      }
      var payload = this.mode === "data"
        ? { title: this.title.trim(), type: "data", viz: this.spec.viz, spec: this.toServerSpec(), scope: this.scope || null }
        : { title: this.title.trim(), type: "content", contentHtml: this.contentHtml || "", scope: this.scope || null };
      var req = this.isEdit
        ? httpClient.put("/report/dashboard-cards/" + this.card.id, payload)
        : httpClient.post("/report/dashboard-cards", payload);
      req.then(function (res) {
        var d = res.data || {};
        if (d.ok && d.card) self.$emit("saved", d.card);
        else self.error = d.message || "Couldn't save the card.";
      })
        .catch(function () { self.error = "Something went wrong saving the card. Please try again."; })
        .finally(function () { self.saving = false; });
    },
    fmtVal(v, format) {
      var n = Number(v) || 0;
      if (format === "money") return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
      if (format === "int") return Math.round(n).toLocaleString("en-US");
      return n.toLocaleString("en-US");
    },
    barPct(v) {
      var items = (this.previewData && this.previewData.data && this.previewData.data.items) || [];
      var max = items.reduce(function (m, it) { return Math.max(m, Number(it.value) || 0); }, 0);
      if (max <= 0) return "0%";
      return Math.max(2, (Number(v) || 0) / max * 100).toFixed(1) + "%";
    },
  },
};
</script>

<style scoped>
.rce-backdrop { position: fixed; inset: 0; background: rgba(20, 18, 19, .55); z-index: 100000; display: flex; align-items: center; justify-content: center; padding: 28px 14px; overflow-y: auto; }
.rce { width: 760px; max-width: 100%; background: #fff; border-radius: 12px; box-shadow: 0 18px 60px rgba(0, 0, 0, .35); display: flex; flex-direction: column; max-height: calc(100vh - 56px); }
.rce-head { display: flex; align-items: center; gap: 10px; padding: 14px 18px; border-bottom: 1px solid #eee; }
.rce-title { font-size: 17px; font-weight: 800; color: #9e2436; flex: 1; }
.rce-x { border: 0; background: none; font-size: 24px; line-height: 1; color: #999; cursor: pointer; padding: 0 4px; }
.rce-x:hover { color: #333; }
.rce-body { padding: 16px 18px; overflow-y: auto; }
.rce-f { display: block; margin-bottom: 12px; }
.rce-f > span { display: block; font-size: 12px; font-weight: 700; color: #555; margin-bottom: 4px; }
.rce-f input[type=text], .rce-f input[type=number], .rce-f select { width: 100%; height: 38px; border: 1px solid #d4d4d8; border-radius: 6px; padding: 0 10px; font-size: 14px; background: #fff; }
.rce-tsearch { margin-bottom: 6px; }
.rce-types { display: flex; gap: 8px; margin: 4px 0 16px; }
.rce-types button { flex: 1; height: 40px; border: 1px solid #d4d4d8; background: #f7f7f8; border-radius: 8px; font-size: 13px; font-weight: 700; color: #555; cursor: pointer; }
.rce-types button.active { background: #9e2436; color: #fff; border-color: #9e2436; }
.rce-tpl-tabs { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 10px; border-bottom: 1px solid #ececef; padding-bottom: 8px; }
.rce-tpl-tab { border: 1px solid #d4d4d8; background: #f7f7f8; color: #555; border-radius: 999px; font-size: 12px; font-weight: 700; padding: 5px 12px; cursor: pointer; }
.rce-tpl-tab.active { background: #9e2436; color: #fff; border-color: #9e2436; }
.rce-tpl-list { display: flex; flex-direction: column; gap: 8px; max-height: 360px; overflow-y: auto; }
.rce-tpl { display: flex; align-items: center; gap: 10px; text-align: left; width: 100%; border: 1px solid #d4d4d8; background: #fff; border-radius: 8px; padding: 12px 14px; cursor: pointer; font-size: 14px; color: #333; }
.rce-tpl:hover { border-color: #9e2436; background: #faf3f4; }
.rce-tpl.is-on { opacity: .6; cursor: default; background: #f6f7f9; }
.rce-tpl.is-on:hover { border-color: #d4d4d8; background: #f6f7f9; }
.rce-tpl-title { flex: 1; font-weight: 700; }
.rce-tpl-badge { flex: 0 0 auto; font-size: 11px; font-weight: 800; padding: 2px 8px; border-radius: 10px; text-transform: uppercase; letter-spacing: .04em; }
.rce-tpl-builtin { background: #eef2ff; color: #3a4ba0; }
.rce-tpl-custom { background: #eafaf1; color: #1f8b4c; }
.rce-tpl-on { background: #e9f7ef; color: #1f8b4c; text-transform: none; letter-spacing: 0; }
.rce-ai { background: #faf3f4; border: 1px solid #f0dfe2; border-radius: 8px; padding: 10px; margin-bottom: 14px; }
.rce-ai-row { display: flex; gap: 8px; }
.rce-ai-row input { flex: 1; height: 38px; border: 1px solid #d4d4d8; border-radius: 6px; padding: 0 10px; font-size: 14px; }
.rce-ai-btn { height: 38px; padding: 0 14px; border: 0; background: #9e2436; color: #fff; font-weight: 700; font-size: 13px; border-radius: 6px; cursor: pointer; white-space: nowrap; }
.rce-ai-btn:disabled { opacity: .6; cursor: default; }
.rce-ai-warn { color: #9e2436; font-size: 12.5px; font-weight: 700; margin-top: 7px; }
.rce-ai-note { color: #8a7a7d; font-size: 11.5px; margin: 7px 2px 0; }
.rce-tokens { background: #f6f7f9; border: 1px solid #e6e6ea; border-radius: 8px; padding: 8px 10px; margin-bottom: 12px; }
.rce-tokens-h { font-size: 11.5px; font-weight: 700; color: #555; margin-bottom: 6px; }
.rce-tokens-h code { background: #ececef; padding: 0 4px; border-radius: 3px; }
.rce-tokens-list { display: flex; flex-wrap: wrap; gap: 6px; max-height: 92px; overflow-y: auto; }
.rce-token { display: inline-flex; align-items: center; gap: 6px; border: 1px solid #d4d4d8; background: #fff; border-radius: 6px; padding: 3px 8px; cursor: pointer; font-size: 11.5px; }
.rce-token:hover { border-color: #9e2436; background: #faf3f4; }
.rce-token-name { color: #9e2436; font-weight: 700; font-family: monospace; }
.rce-token-val { color: #888; max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rce-sec { border: 1px solid #ececec; border-radius: 8px; padding: 10px; margin-bottom: 12px; }
.rce-sec-h { display: flex; align-items: center; margin-bottom: 8px; font-size: 12px; font-weight: 800; color: #555; }
.rce-sec-h em { color: #aaa; font-weight: 600; font-style: normal; }
.rce-sec-tools { margin-left: auto; display: flex; gap: 6px; }
.rce-sec-tools button { border: 1px solid #d4d4d8; background: #f6f6f7; border-radius: 6px; font-size: 12px; font-weight: 700; color: #444; padding: 3px 9px; cursor: pointer; }
.rce-colrow { display: flex; gap: 6px; align-items: center; margin-bottom: 6px; }
.rce-colsel { flex: 2; min-width: 0; height: 34px; border: 1px solid #d4d4d8; border-radius: 6px; font-size: 13px; padding: 0 6px; background: #fff; }
.rce-aggsel { flex: 1; min-width: 0; height: 34px; border: 1px solid #d4d4d8; border-radius: 6px; font-size: 13px; padding: 0 6px; background: #fff; }
.rce-alias { flex: 2; min-width: 0; height: 34px; border: 1px solid #d4d4d8; border-radius: 6px; font-size: 13px; padding: 0 8px; }
.rce-del { flex: 0 0 auto; width: 28px; height: 30px; border: 0; background: none; color: #bbb; font-size: 18px; cursor: pointer; }
.rce-del:hover { color: #9e2436; }
.rce-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 0 12px; }
.rce-previewbar { display: flex; align-items: center; gap: 10px; margin: 4px 0 8px; }
.rce-prev-btn { height: 34px; padding: 0 14px; border: 1px solid #9e2436; background: #fff; color: #9e2436; font-weight: 700; font-size: 13px; border-radius: 6px; cursor: pointer; }
.rce-prev-btn:disabled { opacity: .6; cursor: default; }
.rce-prev-err { color: #c0392b; font-size: 12.5px; font-weight: 600; }
.rce-preview { border: 1px dashed #d8d8dc; border-radius: 8px; padding: 12px; background: #fcfcfd; }
.rce-pv-kpi { font-size: 30px; font-weight: 800; color: #222; text-align: center; }
.rce-pv-kpi small { display: block; font-size: 12px; font-weight: 600; color: #888; margin-top: 2px; }
.rce-pv-bar { display: flex; align-items: center; gap: 8px; margin-bottom: 5px; }
.rce-pv-lbl { flex: 0 0 34%; font-size: 12px; color: #444; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rce-pv-track { flex: 1; height: 14px; background: #efeff1; border-radius: 7px; overflow: hidden; }
.rce-pv-fill { display: block; height: 100%; background: #9e2436; border-radius: 7px; }
.rce-pv-val { flex: 0 0 auto; font-size: 12px; font-weight: 700; color: #333; }
.rce-pv-tablewrap { max-height: 240px; overflow: auto; }
.rce-pv-table { width: 100%; border-collapse: collapse; font-size: 12px; }
.rce-pv-table th { text-align: left; background: #f4f4f5; padding: 5px 8px; position: sticky; top: 0; border-bottom: 1px solid #e4e4e7; }
.rce-pv-table td { padding: 4px 8px; border-bottom: 1px solid #f0f0f2; }
.rce-muted { color: #999; font-size: 12.5px; padding: 4px 2px; }
.rce-foot { display: flex; align-items: center; gap: 10px; padding: 12px 18px; border-top: 1px solid #eee; }
.rce-err { color: #c0392b; font-size: 13px; font-weight: 600; }
.rce-spacer { flex: 1; }
.rce-btn { height: 38px; padding: 0 16px; border: 1px solid #d4d4d8; background: #f6f6f7; color: #333; font-weight: 700; font-size: 13px; border-radius: 6px; cursor: pointer; }
.rce-btn-primary { background: #9e2436; color: #fff; border-color: #9e2436; }
.rce-btn-primary:disabled { opacity: .6; cursor: default; }
.rce-spin { display: inline-block; width: 13px; height: 13px; margin-right: 6px; border: 2px solid rgba(255, 255, 255, .5); border-top-color: #fff; border-radius: 50%; vertical-align: -2px; animation: rce-spin .7s linear infinite; }
.rce-prev-btn .rce-spin { border-color: rgba(158, 36, 54, .35); border-top-color: #9e2436; }
@keyframes rce-spin { to { transform: rotate(360deg); } }
</style>
